import * as React from 'react';
import type {
  ExpertChat,
  Promotion,
  PromotionBlockCode,
  PromotionRepo,
  PromotionTarget,
} from '@/types/api';
import { PROMOTION_BLOCK, PROMOTION_TARGET } from '@/lib/copy';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Panel, PanelBody, PanelHeader } from '@/components/ui/panel';
import { PromotionLedger } from '@/components/ledger/promotion-ledger';
import { useStreamStore, type LivePromotion } from '@/stores/stream-store';
import { PromoteDialog } from './promote-dialog';

/* ---------------------------------------------------------------------------
   THE PROMOTION PANEL.

   Enablement comes from `chat.promotion_gate`, which the SERVER computes.
   Nothing here re-derives it, and that is not a style preference:
   `production_available` is a per-repo git ancestry check across every repo of
   the project, and there is no field on this document from which a browser could
   infer it. A dashboard that guessed would either grey a legal action or offer an
   illegal one, and the second of those ends with a half-shipped change on a
   client's production site. Client-side gating here is cosmetic; the server is
   the real gate and it re-checks on the POST.

   ONE PRIMARY BUTTON. Staging while staging is available, production once
   staging has merged — never both at once. The reviewer's next move should never
   be a choice between two filled buttons.

   THE PARTIAL-FAILURE CASE IS THE DESIGN CENTRE, not the exception. A promotion
   is a sequence of merges that can die at repo 2 of 3, and when it does some
   repos are merged and some are not. The ledger below keeps the merged ones
   visibly merged, names the failing repo at its own stop, and offers no retry —
   because none exists.
--------------------------------------------------------------------------- */

/** Overlay the stream's per-repo progress onto the fetched promotion document. */
function mergeLive(base: Promotion | null | undefined, live: LivePromotion | null): Promotion | null {
  if (!live) return base ?? null;

  if (!base || base.id !== live.promotionId) {
    // The stream knows about a promotion the last fetch did not. Draw what it
    // has told us rather than nothing; the next fetch fills in the rest.
    const repos = Object.values(live.repos).sort((a, b) => a.position - b.position);
    return {
      id: live.promotionId,
      chat_id: 0,
      target: live.target,
      status: live.status,
      repos,
      failure: live.failure,
    };
  }

  const repos: PromotionRepo[] = base.repos.map((repo) => live.repos[repo.repo_id] ?? repo);
  for (const repo of Object.values(live.repos)) {
    if (!repos.some((r) => r.repo_id === repo.repo_id)) repos.push(repo);
  }

  return {
    ...base,
    status: live.status,
    repos: repos.sort((a, b) => a.position - b.position),
    failure: live.failure ?? base.failure ?? null,
  };
}

export function PromotionPanel({ chat, className }: { chat: ExpertChat; className?: string }) {
  const livePromotion = useStreamStore((s) => s.promotion);
  const [dialogTarget, setDialogTarget] = React.useState<PromotionTarget | null>(null);

  const promotion = React.useMemo(() => mergeLive(chat.promotion, livePromotion), [chat.promotion, livePromotion]);

  const gate = chat.promotion_gate ?? {};
  const stagingAvailable = gate.staging_available === true;
  const productionAvailable = gate.production_available === true;
  const reasons = gate.blocked_reasons ?? [];

  const inFlight =
    promotion !== null &&
    (promotion.status === 'preflight' || promotion.status === 'merging' || promotion.status === 'deploying');

  // Staging first: it is the earlier gate and the one that unfreezes the project.
  const primary: PromotionTarget | null = stagingAvailable ? 'staging' : productionAvailable ? 'production' : null;
  const secondary: PromotionTarget | null = stagingAvailable && productionAvailable ? 'production' : null;

  return (
    <Panel className={className}>
      <PanelHeader
        title="Promotion"
        meta={inFlight ? 'in flight' : promotion ? promotion.status : 'none started'}
      />

      <PanelBody className="space-y-3">
        {promotion ? (
          <PromotionLedger promotion={promotion} />
        ) : (
          <p className="text-xs leading-[17px] text-ink-2">
            No promotion has been started on this chat. A promotion merges{' '}
            <span className="font-mono">pre-staging</span> into the target for every repo in position order, one repo
            fully deployed before the next begins.
          </p>
        )}

        {reasons.length > 0 ? <GateReasons reasons={reasons} /> : null}

        <div className="flex flex-wrap items-center gap-2 border-t border-rule pt-3">
          {primary ? (
            <Button variant="primary" disabled={inFlight} onClick={() => setDialogTarget(primary)}>
              {PROMOTION_TARGET[primary].verb}
            </Button>
          ) : (
            <p className="font-mono text-tiny text-ink-3">
              No promotion is available on this chat right now.
            </p>
          )}

          {secondary ? (
            <Button variant="secondary" disabled={inFlight} onClick={() => setDialogTarget(secondary)}>
              {PROMOTION_TARGET[secondary].verb}
            </Button>
          ) : null}

          {inFlight ? (
            <span className="font-mono text-tiny text-action">
              a promotion is running — one per project at a time
            </span>
          ) : null}
        </div>

        {stagingAvailable ? (
          <p className="text-xs leading-[17px] text-ink-3">
            A verified staging merge leaves review and{' '}
            <span className="text-ink-2">unfreezes the project</span> — the client can prompt again while the production
            promotion is still pending.
          </p>
        ) : null}

        <p className="text-xs leading-[17px] text-ink-3">
          These buttons are enabled by the server, not by this page. Production in particular is gated on a per-repo git
          ancestry check across every repo, which no browser can compute.
        </p>
      </PanelBody>

      {dialogTarget ? (
        <PromoteDialog
          chatId={chat.id}
          target={dialogTarget}
          open
          onOpenChange={(open) => {
            if (!open) setDialogTarget(null);
          }}
        />
      ) : null}
    </Panel>
  );
}

function GateReasons({ reasons }: { reasons: PromotionBlockCode[] }) {
  return (
    <div className="border border-rule bg-sunken px-2.5 py-2">
      <p className="eyebrow">Why promotion is blocked</p>
      <ul className="m-0 mt-1.5 list-none space-y-1.5 p-0">
        {reasons.map((code) => {
          const copy = PROMOTION_BLOCK[code];
          return (
            <li key={code} className="flex gap-2">
              <span
                aria-hidden
                className={cn(
                  'mt-[6px] h-[5px] w-[5px] shrink-0 rounded-[1px]',
                  copy.tone === 'danger' ? 'bg-danger' : copy.tone === 'warn' ? 'bg-warn' : 'bg-ink-3',
                )}
              />
              <div className="min-w-0">
                <p className="flex flex-wrap items-baseline gap-x-2 text-xs font-medium text-ink">
                  {copy.label}
                  <span className="font-mono text-tiny text-ink-3">{code}</span>
                </p>
                {copy.detail ? <p className="mt-0.5 text-xs leading-[17px] text-ink-2">{copy.detail}</p> : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
